import React, { StrictMode, useEffect, useRef } from 'react';
import { createRoot } from 'react-dom/client';
import './index.css';
// Load Bootstrap once, in the entry chunk. Dozens of lazy route chunks import it
// individually, so without this its position in the cascade (relative to the module SCSS /
// QuoteStyle.css) depended on which route chunk the user happened to load first.
// The order is deliberate: bootstrap must stay AFTER index.css to preserve the old cascade.
import 'bootstrap/dist/css/bootstrap.min.css';
import App from './App.jsx';
import reportWebVitals from './reportWebVitals';
import { BrowserRouter, useLocation } from 'react-router-dom';
import { trackPageView } from './Utils/tracking';
import { pageView } from './Utils/analytics';

const ThemeContext = React.createContext(null);
const ThemeProvider = ({ children }) => (
    <ThemeContext.Provider value={{}}>
        {children}
    </ThemeContext.Provider>
);

// Sends a GA4 page_view for EVERY route, including the first load (gtag config uses
// send_page_view:false). page_location is origin + pathname only, so query-string PII is never
// sent. The first load's other trackers (GTM pageLoad, fbq PageView, UET pageLoad) are already
// covered by initTracking(); later navigations go through trackPageView (all trackers).
// Pages are lazy-loaded and set document.title after they mount, so the view is sent a moment
// later; otherwise it carries the previous page's title. A page left within that moment (an
// instant redirect) is not counted. The timer is cleared on cleanup, so StrictMode's double
// effect run in development still sends one view.
const PAGE_TITLE_DELAY_MS = 500;
function TrackingRouteListener() {
    const location = useLocation();
    const initialViewSent = useRef(false);

    useEffect(() => {
        const timer = setTimeout(() => {
            if (!initialViewSent.current) {
                initialViewSent.current = true;
                pageView(location.pathname, document.title);
                return;
            }
            trackPageView(location.pathname, document.title);
        }, PAGE_TITLE_DELAY_MS);
        return () => clearTimeout(timer);
    }, [location.pathname]);

    return null;
};

createRoot(document.getElementById('root')).render(
    <StrictMode>
        <ThemeProvider>
            <BrowserRouter>
                <TrackingRouteListener />
                <App />
            </BrowserRouter>
        </ThemeProvider>
    </StrictMode>,
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
